import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const emptyContact = () => ({ id: null, name: '', email: '', phone: '' });
const emptyLocation = () => ({
  id: null,
  wellName: '',
  api: '',
  company: '',
  location: '',
  contacts: [emptyContact()]
});

const CustomerLocationsTab = ({ customerId }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState(emptyLocation());
  const [expandedLocation, setExpandedLocation] = useState(null);

  useEffect(() => {
    if (customerId) {
      fetchLocations();
    }
  }, [customerId]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Guard: skip query if customerId is not a valid UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!customerId || !uuidRegex?.test(customerId)) {
        setLocations([]);
        return;
      }

      const { data, error: fetchError } = await supabase
        ?.from('customer_locations')
        ?.select(`
          *,
          customer_location_contacts(*)
        `)
        ?.eq('customer_id', customerId)
        ?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      const transformed = (data || [])?.map(loc => ({
        id: loc?.id,
        wellName: loc?.well_name,
        api: loc?.api || '',
        company: loc?.company || '',
        location: loc?.location || '',
        contacts: (loc?.customer_location_contacts || [])?.map(c => ({
          id: c?.id,
          name: c?.name,
          email: c?.email || '',
          phone: c?.phone || ''
        }))
      }));

      setLocations(transformed);
    } catch (err) {
      console.error('Error fetching locations:', err?.message);
      setError('Failed to load locations.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingLocation(null);
    setFormData(emptyLocation());
    setShowForm(true);
  };

  const handleEdit = (loc) => {
    setEditingLocation(loc?.id);
    setFormData({
      id: loc?.id,
      wellName: loc?.wellName,
      api: loc?.api,
      company: loc?.company,
      location: loc?.location,
      contacts: loc?.contacts?.length > 0 ? loc?.contacts : [emptyContact()]
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingLocation(null);
    setFormData(emptyLocation());
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleContactChange = (index, field, value) => {
    setFormData(prev => {
      const contacts = [...prev?.contacts];
      contacts[index] = { ...contacts?.[index], [field]: value };
      return { ...prev, contacts };
    });
  };

  const handleAddContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...prev?.contacts, emptyContact()]
    }));
  };

  const handleRemoveContact = (index) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev?.contacts?.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData?.wellName?.trim()) {
      setError('Well Name is required.');
      return;
    }
    try {
      setSaving(true);
      setError(null);

      if (editingLocation) {
        // Update existing location
        const { error: updateError } = await supabase
          ?.from('customer_locations')
          ?.update({
            well_name: formData?.wellName,
            api: formData?.api,
            company: formData?.company,
            location: formData?.location
          })
          ?.eq('id', editingLocation);

        if (updateError) throw updateError;

        // Delete existing contacts and re-insert
        const { error: deleteError } = await supabase
          ?.from('customer_location_contacts')
          ?.delete()
          ?.eq('location_id', editingLocation);

        if (deleteError) throw deleteError;

        const validContacts = formData?.contacts?.filter(c => c?.name?.trim());
        if (validContacts?.length > 0) {
          const { error: insertError } = await supabase
            ?.from('customer_location_contacts')
            ?.insert(validContacts?.map(c => ({
              location_id: editingLocation,
              name: c?.name,
              email: c?.email || null,
              phone: c?.phone || null
            })));

          if (insertError) throw insertError;
        }
      } else {
        // Insert new location
        const { data: newLoc, error: insertError } = await supabase
          ?.from('customer_locations')
          ?.insert({
            customer_id: customerId,
            well_name: formData?.wellName,
            api: formData?.api || null,
            company: formData?.company || null,
            location: formData?.location || null
          })
          ?.select()
          ?.single();

        if (insertError) throw insertError;

        const validContacts = formData?.contacts?.filter(c => c?.name?.trim());
        if (validContacts?.length > 0) {
          const { error: contactError } = await supabase
            ?.from('customer_location_contacts')
            ?.insert(validContacts?.map(c => ({
              location_id: newLoc?.id,
              name: c?.name,
              email: c?.email || null,
              phone: c?.phone || null
            })));

          if (contactError) throw contactError;
        }
      }

      await fetchLocations();
      setShowForm(false);
      setEditingLocation(null);
      setFormData(emptyLocation());
    } catch (err) {
      console.error('Error saving location:', err?.message);
      setError('Failed to save location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Delete this location and all its contacts?')) return;
    try {
      const { error: deleteError } = await supabase
        ?.from('customer_locations')
        ?.delete()
        ?.eq('id', locationId);

      if (deleteError) throw deleteError;
      await fetchLocations();
    } catch (err) {
      console.error('Error deleting location:', err?.message);
      setError('Failed to delete location.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-muted-foreground">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span>Loading locations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-heading font-semibold text-foreground">Locations</h3>
        {!showForm && (
          <Button variant="outline" size="sm" iconName="Plus" iconPosition="left" onClick={handleAddNew}>
            Add Location
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center gap-2">
          <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
          <span className="text-sm text-destructive">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <Icon name="X" size={14} color="var(--color-destructive)" />
          </button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-4">
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <Icon name="MapPin" size={16} color="var(--color-primary)" />
            {editingLocation ? 'Edit Location' : 'New Location'}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Well Name *"
              type="text"
              placeholder="Enter well name"
              value={formData?.wellName}
              onChange={(e) => handleFieldChange('wellName', e?.target?.value)}
            />
            <Input
              label="API"
              type="text"
              placeholder="API number"
              value={formData?.api}
              onChange={(e) => handleFieldChange('api', e?.target?.value)}
            />
            <Input
              label="Company"
              type="text"
              placeholder="Company name"
              value={formData?.company}
              onChange={(e) => handleFieldChange('company', e?.target?.value)}
            />
            <Input
              label="Location"
              type="text"
              placeholder="Location / address"
              value={formData?.location}
              onChange={(e) => handleFieldChange('location', e?.target?.value)}
            />
          </div>

          {/* Contacts Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-medium text-foreground flex items-center gap-2">
                <Icon name="Users" size={14} color="var(--color-primary)" />
                Company Contacts
              </h5>
              <button
                type="button"
                onClick={handleAddContact}
                className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 transition-smooth"
              >
                <Icon name="Plus" size={12} color="currentColor" />
                Add Contact
              </button>
            </div>

            {formData?.contacts?.map((contact, index) => (
              <div key={index} className="bg-card border border-border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-muted-foreground">Contact {index + 1}</span>
                  {formData?.contacts?.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveContact(index)}
                      className="text-destructive hover:text-destructive/80 transition-smooth"
                    >
                      <Icon name="Trash2" size={14} color="currentColor" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Input
                    label="Name"
                    type="text"
                    placeholder="Contact name"
                    value={contact?.name}
                    onChange={(e) => handleContactChange(index, 'name', e?.target?.value)}
                  />
                  <Input
                    label="Email"
                    type="email"
                    placeholder="email@company.com"
                    value={contact?.email}
                    onChange={(e) => handleContactChange(index, 'email', e?.target?.value)}
                  />
                  <Input
                    label="Phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={contact?.phone}
                    onChange={(e) => handleContactChange(index, 'phone', e?.target?.value)}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName={saving ? undefined : 'Save'}
              iconPosition="left"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Location'}
            </Button>
          </div>
        </div>
      )}

      {/* Locations List */}
      {locations?.length === 0 && !showForm ? (
        <div className="text-center py-10 bg-muted/30 rounded-xl border border-dashed border-border">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="MapPin" size={24} color="var(--color-primary)" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">No locations yet</p>
          <p className="text-xs text-muted-foreground mb-3">Add well locations and company contacts for this customer.</p>
          <Button variant="outline" size="sm" iconName="Plus" iconPosition="left" onClick={handleAddNew}>
            Add First Location
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {locations?.map(loc => (
            <div key={loc?.id} className="bg-muted/50 rounded-xl border border-border overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/70 transition-smooth"
                onClick={() => setExpandedLocation(expandedLocation === loc?.id ? null : loc?.id)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="MapPin" size={18} color="var(--color-primary)" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{loc?.wellName}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                      {loc?.company && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Icon name="Building2" size={11} color="currentColor" />
                          {loc?.company}
                        </span>
                      )}
                      {loc?.api && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Icon name="Hash" size={11} color="currentColor" />
                          API: {loc?.api}
                        </span>
                      )}
                      {loc?.contacts?.length > 0 && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Icon name="Users" size={11} color="currentColor" />
                          {loc?.contacts?.length} contact{loc?.contacts?.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                  <button
                    onClick={(e) => { e?.stopPropagation(); handleEdit(loc); }}
                    className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-smooth"
                    title="Edit location"
                  >
                    <Icon name="Edit" size={15} color="currentColor" />
                  </button>
                  <button
                    onClick={(e) => { e?.stopPropagation(); handleDelete(loc?.id); }}
                    className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-smooth"
                    title="Delete location"
                  >
                    <Icon name="Trash2" size={15} color="currentColor" />
                  </button>
                  <Icon
                    name={expandedLocation === loc?.id ? 'ChevronUp' : 'ChevronDown'}
                    size={16}
                    color="var(--color-muted-foreground)"
                  />
                </div>
              </div>

              {expandedLocation === loc?.id && (
                <div className="border-t border-border p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {loc?.api && (
                      <div>
                        <span className="text-muted-foreground">API: </span>
                        <span className="text-foreground font-medium">{loc?.api}</span>
                      </div>
                    )}
                    {loc?.company && (
                      <div>
                        <span className="text-muted-foreground">Company: </span>
                        <span className="text-foreground font-medium">{loc?.company}</span>
                      </div>
                    )}
                    {loc?.location && (
                      <div className="md:col-span-2">
                        <span className="text-muted-foreground">Location: </span>
                        <span className="text-foreground font-medium">{loc?.location}</span>
                      </div>
                    )}
                  </div>

                  {loc?.contacts?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Contacts</p>
                      <div className="space-y-2">
                        {loc?.contacts?.map((contact, idx) => (
                          <div key={idx} className="bg-card rounded-lg p-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                <Icon name="User" size={13} color="var(--color-primary)" />
                              </div>
                              <span className="text-sm font-medium text-foreground">{contact?.name}</span>
                            </div>
                            {contact?.email && (
                              <a
                                href={`mailto:${contact?.email}`}
                                className="text-xs text-primary hover:underline flex items-center gap-1"
                                onClick={(e) => e?.stopPropagation()}
                              >
                                <Icon name="Mail" size={12} color="currentColor" />
                                {contact?.email}
                              </a>
                            )}
                            {contact?.phone && (
                              <a
                                href={`tel:${contact?.phone}`}
                                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                                onClick={(e) => e?.stopPropagation()}
                              >
                                <Icon name="Phone" size={12} color="currentColor" />
                                {contact?.phone}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerLocationsTab;
